import { useState, forwardRef, useImperativeHandle, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Mail, Phone } from "lucide-react";
import { useReveal } from "../hooks/useReveal";

// Relative URL — works in both preview (Kubernetes ingress proxies /api → backend)
// and production (nginx proxies /api → uvicorn on the Riyadh server).
const API = "/api";

const FLEET_SIZES = ["1 – 5 aircraft", "6 – 15 aircraft", "16 – 50 aircraft", "50+ aircraft"];

export const ContactForm = forwardRef(function ContactForm(_props, ref) {
  const innerRef = useRef(null);
  const interestRef = useRef(null);

  const [form, setForm] = useState({
    fullName: "",
    company: "",
    workEmail: "",
    fleetSize: FLEET_SIZES[1],
    interest: "general",
    operationDetails: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useImperativeHandle(ref, () => ({
    focusForm: (interest = "general") => {
      setForm((s) => ({ ...s, interest }));
      innerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  }));

  const revealRef = useReveal();

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Inquiry received. Our team will reach out within a few business days.");
      setForm({
        fullName: "",
        company: "",
        workEmail: "",
        fleetSize: FLEET_SIZES[1],
        interest: form.interest,
        operationDetails: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed. Please retry.";
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      ref={innerRef}
      className="relative py-28 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div ref={revealRef} className="reveal grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left: copy + contact details */}
          <div className="lg:col-span-5">
            <span className="overline">Let's talk</span>
            <h2 className="font-heading mt-4 text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
              Engineer your <span className="gold-text">command center.</span>
            </h2>
            <p className="mt-6 text-white/65 text-lg leading-relaxed">
              Tell us about your fleet. A senior OmniLink solutions architect will reach out within
              a few business days with a tailored briefing.
            </p>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 border border-[#C19A5D]/40 bg-[#121A33] flex items-center justify-center shrink-0">
                  <Mail className="h-4 w-4 text-[#C19A5D]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs text-white/50 uppercase tracking-wider">Email</div>
                  <a href="mailto:solutions@omnilinkweb.org" className="text-white hover:text-[#C19A5D] transition-colors">
                    solutions@omnilinkweb.org
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 border border-[#C19A5D]/40 bg-[#121A33] flex items-center justify-center shrink-0">
                  <Phone className="h-4 w-4 text-[#C19A5D]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs text-white/50 uppercase tracking-wider">Operations Desk</div>
                  <span className="text-white">24/7 — Available to current customers</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <div className="bg-[#121A33]/50 backdrop-blur-sm border border-[#2A3451] p-8 sm:p-10">
              {submitted ? (
                <div data-testid="contact-success" className="py-16 text-center">
                  <CheckCircle2 className="h-12 w-12 text-[#C19A5D] mx-auto" strokeWidth={1.25} />
                  <h3 className="font-heading text-2xl mt-6">Inquiry received.</h3>
                  <p className="text-white/65 mt-3 max-w-md mx-auto">
                    A senior OmniLink architect will be in touch within a few business days. Thank you for your interest.
                  </p>
                  <button
                    data-testid="contact-success-reset"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 text-[#C19A5D] hover:text-[#D4AF74] transition-colors text-sm uppercase tracking-wider font-semibold"
                  >
                    Submit another inquiry →
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="Full name" required>
                      <input
                        data-testid="contact-fullName"
                        required
                        value={form.fullName}
                        onChange={onChange("fullName")}
                        placeholder="Jane Halloran"
                        className="omni-input"
                      />
                    </Field>
                    <Field label="Company" required>
                      <input
                        data-testid="contact-company"
                        required
                        value={form.company}
                        onChange={onChange("company")}
                        placeholder="Atlas Air Group"
                        className="omni-input"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="Work email" required>
                      <input
                        data-testid="contact-workEmail"
                        type="email"
                        required
                        value={form.workEmail}
                        onChange={onChange("workEmail")}
                        placeholder="jane@operator.com"
                        className="omni-input"
                      />
                    </Field>
                    <Field label="Fleet size" required>
                      <select
                        data-testid="contact-fleetSize"
                        value={form.fleetSize}
                        onChange={onChange("fleetSize")}
                        className="omni-input"
                      >
                        {FLEET_SIZES.map((f) => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <Field label="Interest">
                    <div ref={interestRef} className="flex flex-wrap gap-2">
                      {[
                        { v: "general", l: "General inquiry" },
                        { v: "template", l: "Template Plan" },
                        { v: "ecp", l: "ECP Custom" },
                      ].map((opt) => (
                        <button
                          key={opt.v}
                          type="button"
                          data-testid={`contact-interest-${opt.v}`}
                          onClick={() => setForm((s) => ({ ...s, interest: opt.v }))}
                          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold transition-all duration-300 border ${
                            form.interest === opt.v
                              ? "bg-[#C19A5D] text-[#050814] border-[#C19A5D]"
                              : "bg-transparent text-white/70 border-[#2A3451] hover:border-[#C19A5D]/60"
                          }`}
                        >
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <Field label="Operation details" required>
                    <textarea
                      data-testid="contact-operationDetails"
                      required
                      value={form.operationDetails}
                      onChange={onChange("operationDetails")}
                      rows={5}
                      placeholder="Fleet composition, current systems, key objectives…"
                      className="omni-input resize-none"
                    />
                  </Field>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                    <p className="text-xs text-white/40 max-w-sm">
                      By submitting you agree OmniLink may contact you regarding your inquiry. We never share data with third parties.
                    </p>
                    <button
                      data-testid="contact-submit"
                      disabled={submitting}
                      className="group flex items-center justify-center gap-3 bg-[#C19A5D] text-[#050814] hover:bg-[#D4AF74] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 px-8 py-4 font-semibold uppercase tracking-wider text-xs"
                    >
                      {submitting ? "Sending…" : "Send Inquiry"}
                      {!submitting && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .omni-input {
          width: 100%;
          background-color: #0A1128;
          color: #ffffff;
          border: 1px solid #2A3451;
          padding: 0.875rem 1rem;
          font-family: 'Manrope', sans-serif;
          font-size: 0.95rem;
          outline: none;
          transition: border-color .25s ease, box-shadow .25s ease, background-color .25s ease;
        }
        .omni-input:focus {
          border-color: #C19A5D;
          box-shadow: 0 0 0 3px rgba(193, 154, 93, 0.15);
        }
        .omni-input::placeholder { color: rgba(255,255,255,0.35); }
        .omni-input option { background-color: #0A1128; color: #ffffff; }
      `}</style>
    </section>
  );
});

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.18em] text-white/60 font-semibold mb-2">
      {label} {required && <span className="text-[#C19A5D]">*</span>}
    </span>
    {children}
  </label>
);
